import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  AuthStatus,
  EMPTY_AUTH,
  LoginPayload,
  RegisterPayload,
  canRespondCalendar as canRespondCalendarPermission,
  canViewCalendar as canViewCalendarPermission,
  canWriteCalendar as canWriteCalendarPermission,
  hasPermission as hasAuthPermission,
  hasRole as hasAuthRole,
  loadAuthStatus,
  loginUser,
  logoutUser,
  registerUser,
} from '../api/auth';

export type AuthContextValue = {
  apiBaseUrl: string;

  auth: AuthStatus;
  loading: boolean;
  error: string | null;

  refresh: () => Promise<void>;
  login: (payload: LoginPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<string>;
  logout: () => Promise<void>;

  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;

  canViewCalendar: (calendarSlug: string) => boolean;
  canRespondCalendar: (calendarSlug: string) => boolean;
  canWriteCalendar: (calendarSlug: string) => boolean;

  canViewInternal: boolean;
  canViewUserArea: boolean;
  canViewAdmin: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
                               apiBaseUrl,
                               children,
                             }: {
  apiBaseUrl: string;
  children: React.ReactNode;
}) {
  const [auth, setAuth] = useState<AuthStatus>(EMPTY_AUTH);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextAuth = await loadAuthStatus(apiBaseUrl);
      setAuth(nextAuth);
    } catch (refreshError) {
      setAuth(EMPTY_AUTH);
      setError(
          refreshError instanceof Error
              ? refreshError.message
              : 'Auth-Status konnte nicht geladen werden.',
      );
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
      async (payload: LoginPayload) => {
        setError(null);

        const nextAuth = await loginUser(apiBaseUrl, payload);
        setAuth(nextAuth);
      },
      [apiBaseUrl],
  );

  const register = useCallback(
      async (payload: RegisterPayload) => {
        setError(null);

        const result = await registerUser(apiBaseUrl, payload);

        return result.message;
      },
      [apiBaseUrl],
  );

  const logout = useCallback(async () => {
    setError(null);

    try {
      await logoutUser(apiBaseUrl);
    } finally {
      setAuth(EMPTY_AUTH);
    }
  }, [apiBaseUrl]);

  const value = useMemo<AuthContextValue>(() => {
    const hasRole = (role: string): boolean => {
      return hasAuthRole(auth, role);
    };

    const hasPermission = (permission: string): boolean => {
      return hasAuthPermission(auth, permission);
    };

    const canViewCalendar = (calendarSlug: string): boolean => {
      return canViewCalendarPermission(auth, calendarSlug);
    };

    const canRespondCalendar = (calendarSlug: string): boolean => {
      return canRespondCalendarPermission(auth, calendarSlug);
    };

    const canWriteCalendar = (calendarSlug: string): boolean => {
      return canWriteCalendarPermission(auth, calendarSlug);
    };

    const canViewInternal = auth.authenticated === true;

    const canViewUserArea =
        auth.authenticated === true
        && auth.auth_type === 'local'
        && !hasRole('gast');

    const canViewAdmin =
        auth.is_admin === true
        || hasPermission('*')
        || hasRole('admin');

    return {
      apiBaseUrl,

      auth,
      loading,
      error,

      refresh,
      login,
      register,
      logout,

      hasRole,
      hasPermission,

      canViewCalendar,
      canRespondCalendar,
      canWriteCalendar,

      canViewInternal,
      canViewUserArea,
      canViewAdmin,
    };
  }, [
    apiBaseUrl,
    auth,
    error,
    loading,
    login,
    logout,
    refresh,
    register,
  ]);

  return (
      <AuthContext.Provider value={value}>
        {children}
      </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden.');
  }

  return context;
}